<script lang="ts">
  import { Link } from "@inertiajs/svelte";
  import type { Trip } from "../../lib/types";
  import { addNotification } from "../../lib/stores";
  import { formatCurrency } from "../../lib/utils/format";
  import Layout from "../../components/layout/Layout.svelte";

  interface ReportData {
    trip: Trip;
    summary: {
      totalExpenses: number;
      perPersonShare: number;
      participantCount: number;
      durationDays: number;
      categorySummary: Array<{
        category: string;
        amount: number;
        percentage: number;
      }>;
    };
    highlights: Array<{
      type: "expense" | "place" | "photo";
      title: string;
      description: string;
      imageUrl?: string;
    }>;
    expenses: Array<{
      id: string;
      merchantName: string;
      category: string;
      amountCents: number;
      date: string;
    }>;
  }

  let { report, shareToken, user = null }: {
    report: ReportData;
    shareToken: string;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let downloading = $state(false);

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  function getCategoryIcon(category: string): string {
    const icons: Record<string, string> = {
      food: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />`,
      transport: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 17a2 2 0 11-4 0 2 2 0 014 0zm11 0a2 2 0 11-4 0 2 2 0 014 0zM2 9h18M2 9l2-6h12l2 6M2 9v6h18V9" />`,
      accommodation: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />`,
      shopping: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />`,
      activity: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 21l1.9-5.7a8.5 8.5 0 113.8 3.8L3 21" />`,
      other: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />`,
    };
    return icons[category] || icons.other;
  }

  function getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
      food: "식비",
      transport: "교통",
      accommodation: "숙박",
      shopping: "쇼핑",
      activity: "활동",
      other: "기타",
    };
    return labels[category] || "기타";
  }

  async function copyShareLink() {
    const shareUrl = `${window.location.origin}/reports/${shareToken}`;
    await navigator.clipboard.writeText(shareUrl);
    addNotification("success", "공유 링크가 복사되었습니다!");
  }

  async function downloadPDF() {
    downloading = true;
    try {
      // 더미 PDF 다운로드 구현 - 실제 구현 시 서버에서 PDF 생성
      const response = await fetch(`/api/v1/reports/${shareToken}/download?format=pdf`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${report.trip.name}_여행리포트.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        addNotification("success", "PDF 다운로드가 시작되었습니다!");
      } else {
        // 더미 폴백 - 실제로는 서버 API 구현 필요
        addNotification("info", "PDF 다운로드 기능은 준비 중입니다.");
      }
    } catch (error) {
      addNotification("info", "PDF 다운로드 기능은 준비 중입니다.");
    } finally {
      downloading = false;
    }
  }

  async function downloadImage() {
    downloading = true;
    try {
      // 더미 이미지 다운로드 구현
      const response = await fetch(`/api/v1/reports/${shareToken}/download?format=image`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${report.trip.name}_여행리포트.png`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        addNotification("success", "이미지 다운로드가 시작되었습니다!");
      } else {
        addNotification("info", "이미지 다운로드 기능은 준비 중입니다.");
      }
    } catch (error) {
      addNotification("info", "이미지 다운로드 기능은 준비 중입니다.");
    } finally {
      downloading = false;
    }
  }

  function shareToKakao() {
    // 더미 카카오 공유 - 실제 구현 시 Kakao SDK 사용
    addNotification("info", "카카오톡 공유 기능은 준비 중입니다.");
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <!-- Header -->
    <header class="bg-surface border-b border sticky top-0 z-10">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <Link href={`/trips/${report.trip.id}`} class="text-muted hover:text-secondary">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 class="text-xl font-bold text-primary">여행 리포트</h1>
          </div>
          <div class="flex items-center gap-2">
            <button
              onclick={copyShareLink}
              class="flex items-center gap-2 px-4 py-2 bg-surface-hover text-secondary rounded-lg hover:bg-surface-hover transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              링크 복사
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Trip Header Card -->
      <div class="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-6 text-white mb-6">
        <div class="flex items-center gap-2 text-blue-100 text-sm mb-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          </svg>
          {report.trip.destination}
        </div>
        <h2 class="text-3xl font-bold mb-2">{report.trip.name}</h2>
        <p class="text-blue-100">
          {formatDate(report.trip.startDate)} ~ {formatDate(report.trip.endDate)} ({report.summary.durationDays}일)
        </p>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-surface rounded-xl p-4 shadow-sm">
          <p class="text-sm text-muted">총 지출</p>
          <p class="text-2xl font-bold text-primary">₩{formatCurrency(report.summary.totalExpenses)}</p>
        </div>
        <div class="bg-surface rounded-xl p-4 shadow-sm">
          <p class="text-sm text-muted">1인당</p>
          <p class="text-2xl font-bold text-blue-600">₩{formatCurrency(report.summary.perPersonShare)}</p>
        </div>
        <div class="bg-surface rounded-xl p-4 shadow-sm">
          <p class="text-sm text-muted">참여자</p>
          <p class="text-2xl font-bold text-primary">{report.summary.participantCount}명</p>
        </div>
        <div class="bg-surface rounded-xl p-4 shadow-sm">
          <p class="text-sm text-muted">여행 기간</p>
          <p class="text-2xl font-bold text-primary">{report.summary.durationDays}일</p>
        </div>
      </div>

      <!-- Category Breakdown -->
      <div class="bg-surface rounded-2xl p-6 shadow-sm mb-6">
        <h3 class="text-lg font-semibold text-primary mb-4">카테고리별 지출</h3>
        <div class="space-y-4">
          {#each report.summary.categorySummary as category}
            <div class="flex items-center gap-4">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">{@html getCategoryIcon(category.category)}</svg>
              <div class="flex-1">
                <div class="flex justify-between mb-1">
                  <span class="font-medium text-primary">{getCategoryLabel(category.category)}</span>
                  <span class="text-secondary">₩{formatCurrency(category.amount)}</span>
                </div>
                <div class="w-full bg-surface-hover rounded-full h-2">
                  <div
                    class="bg-blue-500 rounded-full h-2 transition-all"
                    style="width: {category.percentage}%"
                  ></div>
                </div>
              </div>
              <span class="text-sm text-muted w-12 text-right">{category.percentage}%</span>
            </div>
          {/each}
        </div>
      </div>

      <!-- Highlights -->
      {#if report.highlights && report.highlights.length > 0}
        <div class="bg-surface rounded-2xl p-6 shadow-sm mb-6">
          <h3 class="text-lg font-semibold text-primary mb-4">여행 하이라이트</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            {#each report.highlights as highlight}
              <div class="bg-surface-hover rounded-xl overflow-hidden">
                {#if highlight.imageUrl}
                  <img src={highlight.imageUrl} alt="" class="w-full h-32 object-cover" />
                {:else}
                  <div class="w-full h-32 bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                    {#if highlight.type === "expense"}
                      <svg class="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    {:else if highlight.type === "place"}
                      <svg class="w-12 h-12 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {:else}
                      <svg class="w-12 h-12 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {/if}
                  </div>
                {/if}
                <div class="p-3">
                  <h4 class="font-medium text-primary">{highlight.title}</h4>
                  <p class="text-sm text-muted">{highlight.description}</p>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Recent Expenses -->
      <div class="bg-surface rounded-2xl p-6 shadow-sm mb-6">
        <h3 class="text-lg font-semibold text-primary mb-4">주요 지출 내역</h3>
        <div class="space-y-3">
          {#each report.expenses.slice(0, 10) as expense}
            <div class="flex items-center justify-between py-2 border-b border last:border-0">
              <div class="flex items-center gap-3">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">{@html getCategoryIcon(expense.category)}</svg>
                <div>
                  <p class="font-medium text-primary">{expense.merchantName}</p>
                  <p class="text-xs text-muted">{formatDate(expense.date)}</p>
                </div>
              </div>
              <p class="font-medium text-primary">₩{formatCurrency(expense.amountCents)}</p>
            </div>
          {/each}
        </div>
      </div>

      <!-- Download & Share Actions -->
      <div class="bg-surface rounded-2xl p-6 shadow-sm">
        <h3 class="text-lg font-semibold text-primary mb-4">리포트 공유하기</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onclick={downloadPDF}
            disabled={downloading}
            class="flex flex-col items-center gap-2 p-4 bg-red-50 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span class="text-sm font-medium text-red-700">PDF 저장</span>
          </button>

          <button
            onclick={downloadImage}
            disabled={downloading}
            class="flex flex-col items-center gap-2 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span class="text-sm font-medium text-blue-700">이미지 저장</span>
          </button>

          <button
            onclick={shareToKakao}
            class="flex flex-col items-center gap-2 p-4 bg-yellow-50 rounded-xl hover:bg-yellow-100 transition-colors"
          >
            <svg class="w-8 h-8 text-yellow-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3C6.5 3 2 6.58 2 11c0 2.83 1.82 5.3 4.54 6.67-.2.73-.72 2.67-.83 3.08-.13.5.18.49.38.36.16-.1 2.49-1.69 3.5-2.37.46.06.93.09 1.41.09 5.5 0 10-3.58 10-8S17.5 3 12 3z" />
            </svg>
            <span class="text-sm font-medium text-yellow-700">카카오톡</span>
          </button>

          <button
            onclick={copyShareLink}
            class="flex flex-col items-center gap-2 p-4 bg-surface-hover rounded-xl hover:bg-surface-hover transition-colors"
          >
            <svg class="w-8 h-8 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span class="text-sm font-medium text-secondary">링크 복사</span>
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="text-center py-8">
        <p class="text-sm text-muted">
          TripHelper에서 생성된 여행 리포트입니다
        </p>
      </div>
    </main>
  </div>
</Layout>
